"use client"

import * as React from 'react'
import { useParams, useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { deleteCrud, updateCrud } from '@open-mercato/ui/backend/utils/crud'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type SlotDetail = {
  id: string
  stationId: string
  stationName: string | null
  slotType: string
  status: string
  trackNumber: number
  platform: string | null
  startTime: string
  endTime: string
  trainPlanId: string | null
  harmonogramEntryId: string | null
  cargoDescription: string | null
  notes: string | null
}

type RouteParams = {
  id?: string | string[]
  slug?: string[]
}

function readRouteId(params?: RouteParams | null): string {
  if (!params) return ''
  if (typeof params.id === 'string') return params.id
  if (Array.isArray(params.id)) return params.id[0] ?? ''
  if (Array.isArray(params.slug)) return params.slug.at(-1) ?? ''
  return ''
}

function mapSlotDetail(item: Record<string, unknown>): SlotDetail | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    stationId: typeof item.station_id === 'string' ? item.station_id : '',
    stationName: typeof item.station_name === 'string' ? item.station_name : null,
    slotType: typeof item.slot_type === 'string' ? item.slot_type : '',
    status: typeof item.status === 'string' ? item.status : 'planned',
    trackNumber: typeof item.track_number === 'number' ? item.track_number : 0,
    platform: typeof item.platform === 'string' ? item.platform : null,
    startTime: typeof item.start_time === 'string' ? item.start_time : '',
    endTime: typeof item.end_time === 'string' ? item.end_time : '',
    trainPlanId: typeof item.train_plan_id === 'string' ? item.train_plan_id : null,
    harmonogramEntryId: typeof item.harmonogram_entry_id === 'string' ? item.harmonogram_entry_id : null,
    cargoDescription: typeof item.cargo_description === 'string' ? item.cargo_description : null,
    notes: typeof item.notes === 'string' ? item.notes : null,
  }
}

function toDateTimeLocal(iso: string | null): string {
  if (!iso) return ''
  return iso.slice(0, 16)
}

export default function TerminalSlotDetailPage({ params }: { params?: RouteParams }) {
  const t = useT()
  const router = useRouter()
  const routeParams = useParams()
  const slotId = readRouteId(params) || readRouteId(routeParams as RouteParams | undefined)
  const scopeVersion = useOrganizationScopeVersion()

  const [slot, setSlot] = React.useState<SlotDetail | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [loadError, setLoadError] = React.useState<string | null>(null)

  const getSlotTypeLabel = React.useCallback((slotType: string) => {
    const fallbackMap: Record<string, string> = {
      arrival: 'Arrival',
      departure: 'Departure',
      shunting: 'Shunting',
    }
    return t(`terminal_scheduling.slots.type.${slotType}`, fallbackMap[slotType] ?? slotType)
  }, [t])

  const getStatusLabel = React.useCallback((status: string) => {
    const fallbackMap: Record<string, string> = {
      planned: 'Planned',
      booked: 'Booked',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    }
    return t(`terminal_scheduling.slots.status.${status}`, fallbackMap[status] ?? status)
  }, [t])

  const loadSlot = React.useCallback(async () => {
    if (!slotId) {
      setSlot(null)
      setLoadError(t('terminal_scheduling.slots.error.notFound', 'Terminal slot not found'))
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setLoadError(null)

    try {
      type ResponseType = { items: Array<Record<string, unknown>>; error?: string }
      const fallback: ResponseType = { items: [] }
      const call = await apiCall<ResponseType>(
        `/api/terminal_scheduling/slots?ids=${encodeURIComponent(slotId)}&pageSize=1`,
        undefined,
        { fallback },
      )

      if (!call.ok) {
        setSlot(null)
        setLoadError(
          typeof call.result?.error === 'string'
            ? call.result.error
            : t('terminal_scheduling.slots.error.load', 'Failed to load terminal slots'),
        )
        return
      }

      const item = Array.isArray(call.result?.items) ? call.result.items[0] : null
      const mapped = item ? mapSlotDetail(item) : null
      if (!mapped) {
        setSlot(null)
        setLoadError(t('terminal_scheduling.slots.error.notFound', 'Terminal slot not found'))
        return
      }

      setSlot(mapped)
    } catch (error) {
      setSlot(null)
      setLoadError(
        error instanceof Error && error.message
          ? error.message
          : t('terminal_scheduling.slots.error.load', 'Failed to load terminal slots'),
      )
    } finally {
      setIsLoading(false)
    }
  }, [slotId, t])

  React.useEffect(() => {
    void loadSlot()
  }, [loadSlot, scopeVersion])

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'stationDisplay',
      label: t('terminal_scheduling.fields.stationId', 'Station'),
      type: 'text',
      readOnly: true,
    },
    {
      id: 'slotType',
      label: t('terminal_scheduling.fields.slotType', 'Slot Type'),
      type: 'select',
      required: true,
      options: [
        { value: 'arrival', label: t('terminal_scheduling.slots.type.arrival', 'Arrival') },
        { value: 'departure', label: t('terminal_scheduling.slots.type.departure', 'Departure') },
        { value: 'shunting', label: t('terminal_scheduling.slots.type.shunting', 'Shunting') },
      ],
    },
    {
      id: 'status',
      label: t('terminal_scheduling.slots.columns.status', 'Status'),
      type: 'text',
      readOnly: true,
    },
    {
      id: 'trackNumber',
      label: t('terminal_scheduling.fields.trackNumber', 'Track Number'),
      type: 'number',
      required: true,
    },
    {
      id: 'platform',
      label: t('terminal_scheduling.fields.platform', 'Platform'),
      type: 'text',
    },
    {
      id: 'startTime',
      label: t('terminal_scheduling.fields.startTime', 'Start Time'),
      type: 'datetime-local',
      required: true,
    },
    {
      id: 'endTime',
      label: t('terminal_scheduling.fields.endTime', 'End Time'),
      type: 'datetime-local',
      required: true,
    },
    {
      id: 'trainPlanId',
      label: t('terminal_scheduling.fields.trainPlanId', 'Train Plan ID'),
      type: 'text',
      placeholder: 'Optional UUID',
    },
    {
      id: 'harmonogramEntryId',
      label: t('terminal_scheduling.fields.harmonogramEntryId', 'Harmonogram Entry ID'),
      type: 'text',
      readOnly: true,
    },
    {
      id: 'cargoDescription',
      label: t('terminal_scheduling.fields.cargoDescription', 'Cargo Description'),
      type: 'textarea',
    },
    {
      id: 'notes',
      label: t('terminal_scheduling.fields.notes', 'Notes'),
      type: 'textarea',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'location',
      title: t('terminal_scheduling.groups.location', 'Location'),
      fields: ['stationDisplay', 'trackNumber', 'platform'],
    },
    {
      id: 'timing',
      title: t('terminal_scheduling.groups.timing', 'Timing'),
      fields: ['slotType', 'status', 'startTime', 'endTime'],
    },
    {
      id: 'details',
      title: t('terminal_scheduling.groups.details', 'Details'),
      fields: ['trainPlanId', 'harmonogramEntryId', 'cargoDescription', 'notes'],
    },
  ], [t])

  if (isLoading) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('terminal_scheduling.slots.loading', 'Loading terminal slot...')} />
        </PageBody>
      </Page>
    )
  }

  if (loadError || !slot) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage label={loadError ?? t('terminal_scheduling.slots.error.notFound', 'Terminal slot not found')} />
        </PageBody>
      </Page>
    )
  }

  const statusLabel = getStatusLabel(slot.status)
  const slotTypeLabel = getSlotTypeLabel(slot.slotType)

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={`${t('terminal_scheduling.slots.detail.title', 'Terminal Slot')} - ${slotTypeLabel} / ${statusLabel}`}
          backHref="/backend/terminal-scheduling"
          fields={fields}
          groups={groups}
          initialValues={{
            stationDisplay: slot.stationName ?? slot.stationId,
            slotType: slot.slotType,
            status: statusLabel,
            trackNumber: slot.trackNumber,
            platform: slot.platform ?? '',
            startTime: toDateTimeLocal(slot.startTime),
            endTime: toDateTimeLocal(slot.endTime),
            trainPlanId: slot.trainPlanId ?? '',
            harmonogramEntryId: slot.harmonogramEntryId ?? '',
            cargoDescription: slot.cargoDescription ?? '',
            notes: slot.notes ?? '',
          }}
          submitLabel={t('terminal_scheduling.slots.actions.update', 'Save Changes')}
          cancelHref="/backend/terminal-scheduling"
          onDelete={async () => {
            await deleteCrud('terminal_scheduling/slots', { id: slot.id })
            flash(t('terminal_scheduling.slots.flash.deleted', 'Terminal slot deleted'), 'success')
            router.push('/backend/terminal-scheduling')
          }}
          deleteRedirect="/backend/terminal-scheduling"
          onSubmit={async (values) => {
            const { stationDisplay: _ignored, ...rest } = values
            await updateCrud('terminal_scheduling/slots', { id: slot.id, stationId: slot.stationId, ...rest })
            flash(t('terminal_scheduling.slots.flash.updated', 'Terminal slot updated'), 'success')
            await loadSlot()
          }}
        />
      </PageBody>
    </Page>
  )
}
